// Vector-drawing primitives on top of jsPDF. Used by the PDF generator
// to render charts and tiles natively — no html2canvas, no rasterisation,
// crisp at any zoom.
//
// All units are millimetres (jsPDF unit: 'mm'). Y axis is top-down per
// jsPDF convention (y=0 at the top of the page).
//
// Colour convention: tuples [r, g, b] in 0–255. Matches jsPDF's
// setFillColor / setDrawColor signature.

// Loose typing for jsPDF — we don't import the full type so this file
// stays usable without bringing the lib's type surface across the
// public boundary. The PDF generator narrows on call.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Pdf = any;
export type RGB = [number, number, number];
export type Box = { x: number; y: number; w: number; h: number };

// ── Brand palette ──────────────────────────────────────────────────────
export const COLOR = {
  bg:       [10, 12, 16] as RGB,
  panel:    [15, 17, 21] as RGB,
  line:     [35, 42, 53] as RGB,
  lineSoft: [26, 32, 41] as RGB,
  text:     [243, 244, 246] as RGB,
  textMid:  [156, 163, 175] as RGB,
  textDim:  [107, 114, 128] as RGB,
  green:    [0, 255, 136] as RGB,
  greenDark:[16, 185, 129] as RGB,
  red:      [239, 68, 68] as RGB,
  blue:     [96, 165, 250] as RGB,
  amber:    [245, 158, 11] as RGB,
};

// ── Helpers ───────────────────────────────────────────────────────────
export function setFill(pdf: Pdf, c: RGB) { pdf.setFillColor(c[0], c[1], c[2]); }
export function setStroke(pdf: Pdf, c: RGB) { pdf.setDrawColor(c[0], c[1], c[2]); }
export function setText(pdf: Pdf, c: RGB) { pdf.setTextColor(c[0], c[1], c[2]); }

// Draw a filled rounded rectangle, used for KPI tile backgrounds. jsPDF
// has roundedRect built in — we wrap it to set fill/stroke in one call.
export function panel(pdf: Pdf, b: Box, opts: { fill?: RGB; stroke?: RGB; r?: number } = {}) {
  const r = opts.r ?? 1.5;
  if (opts.fill) {
    setFill(pdf, opts.fill);
    if (opts.stroke) setStroke(pdf, opts.stroke);
    pdf.roundedRect(b.x, b.y, b.w, b.h, r, r, opts.stroke ? 'FD' : 'F');
  } else if (opts.stroke) {
    setStroke(pdf, opts.stroke);
    pdf.roundedRect(b.x, b.y, b.w, b.h, r, r, 'S');
  }
}

// ── KPI tile ───────────────────────────────────────────────────────────
export function kpiTile(
  pdf: Pdf, b: Box,
  opts: {
    label: string;
    value: string;
    sub?: string;
    valueColor?: RGB;
    accent?: RGB;       // small left-bar accent stripe
  },
) {
  // Background panel
  panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line, r: 1.5 });

  // Accent stripe (2px wide, 70% height, left side)
  if (opts.accent) {
    setFill(pdf, opts.accent);
    pdf.rect(b.x, b.y + 1.5, 0.6, b.h - 3, 'F');
  }

  // Label (uppercase, dim)
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6);
  setText(pdf, COLOR.textMid);
  pdf.text(opts.label.toUpperCase(), b.x + 3, b.y + 4);

  // Value (large)
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  setText(pdf, opts.valueColor ?? COLOR.text);
  pdf.text(opts.value, b.x + 3, b.y + 9);

  // Sub
  if (opts.sub) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6.5);
    setText(pdf, COLOR.textDim);
    pdf.text(opts.sub, b.x + 3, b.y + 12.5);
  }
}

// ── Section title ─────────────────────────────────────────────────────
export function sectionTitle(pdf: Pdf, x: number, y: number, w: number, label: string, sub?: string) {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7);
  setText(pdf, COLOR.text);
  pdf.text(label.toUpperCase(), x, y);

  if (sub) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    setText(pdf, COLOR.textDim);
    const lw = pdf.getTextWidth(label.toUpperCase());
    pdf.text(sub, x + lw + 3, y);
  }

  // Underline
  setStroke(pdf, COLOR.line);
  pdf.setLineWidth(0.15);
  pdf.line(x, y + 1.5, x + w, y + 1.5);
}

// ── Cartesian chart axes ──────────────────────────────────────────────
// Draws axes, gridlines, and tick labels inside a Box. Returns:
//   plotBox  — the inner rectangle where data should be drawn
//   xToMm    — function mapping a data X value to PDF X coordinate
//   yToMm    — function mapping a data Y value to PDF Y coordinate
//
// The plot area shrinks from `b` by AXIS_LEFT / AXIS_BOTTOM padding to
// leave room for tick labels.

export type AxisOpts = {
  xMin: number; xMax: number;
  yMin: number; yMax: number;
  xTicks?: { v: number; label: string }[];
  yTicks?: { v: number; label: string }[];
  // Optional padding overrides (mm)
  axisLeft?: number;
  axisBottom?: number;
  axisRight?: number;
  axisTop?: number;
};

const AXIS_LEFT_DEFAULT = 14;
const AXIS_BOTTOM_DEFAULT = 7;
const AXIS_RIGHT_DEFAULT = 3;
const AXIS_TOP_DEFAULT = 3;

export function drawCartesian(pdf: Pdf, b: Box, opts: AxisOpts) {
  const aL = opts.axisLeft ?? AXIS_LEFT_DEFAULT;
  const aB = opts.axisBottom ?? AXIS_BOTTOM_DEFAULT;
  const aR = opts.axisRight ?? AXIS_RIGHT_DEFAULT;
  const aT = opts.axisTop ?? AXIS_TOP_DEFAULT;

  const plotBox: Box = {
    x: b.x + aL,
    y: b.y + aT,
    w: b.w - aL - aR,
    h: b.h - aT - aB,
  };

  const xRange = Math.max(1e-9, opts.xMax - opts.xMin);
  const yRange = Math.max(1e-9, opts.yMax - opts.yMin);

  const xToMm = (v: number) => plotBox.x + ((v - opts.xMin) / xRange) * plotBox.w;
  const yToMm = (v: number) => plotBox.y + plotBox.h - ((v - opts.yMin) / yRange) * plotBox.h;

  // Background panel
  panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line, r: 1.5 });

  // Horizontal gridlines via Y ticks
  setStroke(pdf, COLOR.lineSoft);
  pdf.setLineWidth(0.1);
  setText(pdf, COLOR.textMid);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6);

  if (opts.yTicks) {
    for (const t of opts.yTicks) {
      const y = yToMm(t.v);
      // Skip if out of plot area (slight tolerance)
      if (y < plotBox.y - 0.1 || y > plotBox.y + plotBox.h + 0.1) continue;
      pdf.line(plotBox.x, y, plotBox.x + plotBox.w, y);
      pdf.text(t.label, plotBox.x - 1.5, y + 1.2, { align: 'right' });
    }
  }

  // X tick labels (no vertical gridlines — keeps charts cleaner)
  if (opts.xTicks) {
    for (const t of opts.xTicks) {
      const x = xToMm(t.v);
      if (x < plotBox.x - 0.1 || x > plotBox.x + plotBox.w + 0.1) continue;
      pdf.text(t.label, x, plotBox.y + plotBox.h + 3.5, { align: 'center' });
      // tiny tick mark
      setStroke(pdf, COLOR.line);
      pdf.line(x, plotBox.y + plotBox.h, x, plotBox.y + plotBox.h + 0.8);
    }
  }

  return { plotBox, xToMm, yToMm };
}

// ── Line series ───────────────────────────────────────────────────────
// _plotBox is currently unused but kept in the signature so callers can
// later pass it for clipping (jsPDF clip via path) without an API change.
export function drawLineSeries(
  pdf: Pdf, _plotBox: Box,
  points: { x: number; y: number }[],
  xToMm: (v: number) => number,
  yToMm: (v: number) => number,
  opts: { color: RGB; width?: number; dashed?: boolean } = { color: COLOR.green },
) {
  if (points.length < 2) return;

  setStroke(pdf, opts.color);
  pdf.setLineWidth(opts.width ?? 0.4);
  if (opts.dashed) pdf.setLineDashPattern([1, 1], 0);

  // Clip-by-skip approach: use jsPDF's rect-based clipping is overkill.
  // Just draw line segments, dropping any outside the plot box.
  let prevX = xToMm(points[0].x);
  let prevY = yToMm(points[0].y);
  for (let i = 1; i < points.length; i++) {
    const x = xToMm(points[i].x);
    const y = yToMm(points[i].y);
    pdf.line(prevX, prevY, x, y);
    prevX = x; prevY = y;
  }

  if (opts.dashed) pdf.setLineDashPattern([], 0);
}

// ── Filled area (e.g. drawdown) ───────────────────────────────────────
export function drawAreaSeries(
  pdf: Pdf, plotBox: Box,
  points: { x: number; y: number }[],
  baselineY: number,
  xToMm: (v: number) => number,
  yToMm: (v: number) => number,
  opts: { fill: RGB; stroke?: RGB; opacity?: number } = { fill: COLOR.red },
) {
  if (points.length < 2) return;

  // Build polygon: first point bottom, walk up the line, last point bottom.
  setFill(pdf, opts.fill);
  // jsPDF supports GState for opacity
  const gstate = pdf.GState ? new pdf.GState({ opacity: opts.opacity ?? 0.35 }) : null;
  if (gstate) pdf.setGState(gstate);

  const yBase = yToMm(baselineY);
  const lines: [number, number][] = [];

  // Move to first point on baseline, then to first data point
  const startX = xToMm(points[0].x);
  for (let i = 0; i < points.length; i++) {
    const x = xToMm(points[i].x);
    const y = yToMm(points[i].y);
    lines.push([x, y]);
  }
  // Close back to baseline
  const endX = xToMm(points[points.length - 1].x);

  // Use jsPDF's lines() for a polygon — it expects relative coords.
  // We'll build absolute then convert.
  const startPoint: [number, number] = [startX, yBase];
  const rel: number[][] = [];
  rel.push([startX - startX, lines[0][1] - yBase]);  // up to first
  for (let i = 1; i < lines.length; i++) {
    rel.push([lines[i][0] - lines[i - 1][0], lines[i][1] - lines[i - 1][1]]);
  }
  rel.push([endX - lines[lines.length - 1][0], yBase - lines[lines.length - 1][1]]);  // back to baseline

  pdf.lines(rel, startPoint[0], startPoint[1], [1, 1], 'F', true);

  if (gstate) {
    const reset = new pdf.GState({ opacity: 1 });
    pdf.setGState(reset);
  }

  // Stroke the top line for definition
  if (opts.stroke) {
    drawLineSeries(pdf, plotBox, points, xToMm, yToMm, { color: opts.stroke, width: 0.4 });
  }
}

// ── Bar chart ─────────────────────────────────────────────────────────
// _plotBox kept in signature for future clip support — see drawLineSeries.
export function drawBars(
  pdf: Pdf, _plotBox: Box,
  bars: { x: number; v: number; color: RGB }[],
  xToMm: (v: number) => number,
  yToMm: (v: number) => number,
  opts: { barW?: number; baseline?: number } = {},
) {
  if (bars.length === 0) return;

  // Auto-compute bar width: 70% of column width
  let barW = opts.barW;
  if (barW == null && bars.length > 1) {
    const colW = (xToMm(bars[1].x) - xToMm(bars[0].x));
    barW = Math.abs(colW) * 0.7;
  }
  barW = barW ?? 4;

  const baseline = opts.baseline ?? 0;
  const yBase = yToMm(baseline);

  for (const bar of bars) {
    const cx = xToMm(bar.x);
    const yTop = yToMm(bar.v);
    const x = cx - barW / 2;
    const y = Math.min(yTop, yBase);
    const h = Math.abs(yTop - yBase);
    if (h < 0.05) continue;
    setFill(pdf, bar.color);
    pdf.rect(x, y, barW, h, 'F');
  }
}

// ── Calendar heatmap ──────────────────────────────────────────────────
export function drawMonthGrid(
  pdf: Pdf, b: Box, ym: string,
  buckets: Map<string, { profit: number; trades: number }>,
  maxAbs: number,
) {
  // Panel + month label
  panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line, r: 1.5 });

  const [yyStr, mmStr] = ym.split('-');
  const yy = Number(yyStr); const mm = Number(mmStr);
  const first = new Date(Date.UTC(yy, mm - 1, 1));
  const daysInMonth = new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  const startDow = first.getUTCDay();

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6.5);
  setText(pdf, COLOR.textMid);
  const monthName = first.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  pdf.text(monthName.toUpperCase(), b.x + 2, b.y + 4);

  // Day-of-week header
  const headerY = b.y + 7;
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S']; // Monday-first
  const cellW = (b.w - 4) / 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(5);
  setText(pdf, COLOR.textDim);
  for (let i = 0; i < 7; i++) {
    pdf.text(days[i], b.x + 2 + i * cellW + cellW / 2, headerY, { align: 'center' });
  }

  // Cells. JS Date Sunday=0, but we render Monday-first to match the live page.
  // Convert: Mon=0, Tue=1, ... Sun=6.
  const startDowMon = (startDow + 6) % 7;
  const gridY = headerY + 1.5;
  const cellH = cellW; // square-ish

  pdf.setFontSize(5);
  for (let d = 1; d <= daysInMonth; d++) {
    const cellIdx = startDowMon + (d - 1);
    const row = Math.floor(cellIdx / 7);
    const col = cellIdx % 7;
    const x = b.x + 2 + col * cellW;
    const y = gridY + row * cellH;

    const ds = `${ym}-${String(d).padStart(2, '0')}`;
    const bucket = buckets.get(ds);
    const profit = bucket?.profit ?? 0;

    // Tint
    if (profit !== 0 && maxAbs > 0) {
      const intensity = Math.min(1, Math.abs(profit) / maxAbs);
      const a = 0.18 + 0.65 * intensity;
      const base: RGB = profit > 0 ? COLOR.green : COLOR.red;
      // jsPDF doesn't blend rgba easily — approximate by blending toward bg
      const blend: RGB = [
        Math.round(base[0] * a + COLOR.bg[0] * (1 - a)),
        Math.round(base[1] * a + COLOR.bg[1] * (1 - a)),
        Math.round(base[2] * a + COLOR.bg[2] * (1 - a)),
      ];
      setFill(pdf, blend);
      pdf.rect(x, y, cellW - 0.4, cellH - 0.4, 'F');
    } else {
      setStroke(pdf, COLOR.lineSoft);
      pdf.setLineWidth(0.08);
      pdf.rect(x, y, cellW - 0.4, cellH - 0.4, 'S');
    }
    setText(pdf, profit !== 0 ? COLOR.text : COLOR.textDim);
    pdf.text(String(d), x + cellW / 2, y + cellH * 0.65, { align: 'center' });
  }
}

// ── Simple table primitive ────────────────────────────────────────────
// Used for journal, tag performance, mistake correlation.
export type TableCol = {
  label: string;
  width: number;             // mm
  align?: 'left' | 'right' | 'center';
};

export type TableRow = (string | { text: string; color?: RGB; bold?: boolean })[];

export function drawTable(
  pdf: Pdf,
  x: number, y: number,
  cols: TableCol[],
  rows: TableRow[],
  opts: {
    headerSize?: number;
    bodySize?: number;
    rowHeight?: number;
    onPageBreak?: () => number;   // returns the new y after page break (caller redraws header)
    pageLimit?: number;           // max y before breaking
  } = {},
): number {
  const headerSize = opts.headerSize ?? 6.5;
  const bodySize = opts.bodySize ?? 7;
  const rowH = opts.rowHeight ?? 4.6;

  // Calculate column x offsets
  const colX: number[] = [];
  let cursor = x;
  for (const c of cols) {
    colX.push(cursor);
    cursor += c.width;
  }
  const totalW = cursor - x;

  const drawHeader = (atY: number) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(headerSize);
    setText(pdf, COLOR.textMid);
    for (let i = 0; i < cols.length; i++) {
      const cx = cols[i].align === 'right' ? colX[i] + cols[i].width - 1
              : cols[i].align === 'center' ? colX[i] + cols[i].width / 2
              : colX[i];
      pdf.text(cols[i].label.toUpperCase(), cx, atY, { align: cols[i].align ?? 'left' });
    }
    setStroke(pdf, COLOR.line);
    pdf.setLineWidth(0.15);
    pdf.line(x, atY + 1.2, x + totalW, atY + 1.2);
    return atY + 4;
  };

  let cy = drawHeader(y);

  for (const row of rows) {
    if (opts.onPageBreak && opts.pageLimit && cy + rowH > opts.pageLimit) {
      cy = opts.onPageBreak();
      cy = drawHeader(cy);
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(bodySize);

    for (let i = 0; i < cols.length; i++) {
      const cellRaw = row[i];
      const cell = typeof cellRaw === 'string' ? { text: cellRaw } : cellRaw;
      const cx = cols[i].align === 'right' ? colX[i] + cols[i].width - 1
              : cols[i].align === 'center' ? colX[i] + cols[i].width / 2
              : colX[i];
      setText(pdf, cell.color ?? COLOR.text);
      if (cell.bold) pdf.setFont('helvetica', 'bold'); else pdf.setFont('helvetica', 'normal');
      pdf.text(cell.text ?? '', cx, cy, { align: cols[i].align ?? 'left' });
    }

    // Subtle row separator
    setStroke(pdf, COLOR.lineSoft);
    pdf.setLineWidth(0.05);
    pdf.line(x, cy + rowH - 2.5, x + totalW, cy + rowH - 2.5);

    cy += rowH;
  }

  return cy;
}

// ── Tick generator ────────────────────────────────────────────────────
// Picks "nice" Y-axis ticks given a min, max, and target count.
export function niceTicks(min: number, max: number, target = 5): number[] {
  if (max <= min) return [min];
  const range = max - min;
  const rough = range / target;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / pow;
  let nice: number;
  if (norm < 1.5) nice = 1;
  else if (norm < 3) nice = 2;
  else if (norm < 7) nice = 5;
  else nice = 10;
  const step = nice * pow;
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max + 1e-9; v += step) out.push(Math.round(v / step) * step);
  return out;
}
